if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = this.variantIdInput.disabled || this.dataset.monoreeSizePending === 'true';
        if (this.variantIdInput.disabled) this.variantIdInput.value = '';
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.dataset.monoreeSizePending === 'true') {
          const picker = this.closest('product-info')?.querySelector('[data-monoree-size-option]');
          picker?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          picker?.querySelector('input:not(:disabled), select')?.focus({ preventScroll: true });
          return;
        }
        if (this.dataset.monoreeVariantPending === 'true' || this.variantIdInput.disabled ||
            !this.variantIdInput.value || this.submitButton.disabled || this.submitInProgress ||
            this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const quantityInput = this.form.elements.namedItem('quantity');
        if (quantityInput && (Number(quantityInput.value) <= 0 || quantityInput.validity?.valid === false)) {
          quantityInput.reportValidity();
          return;
        }
        if (this.cartNeedsReview || (typeof CartItems !== 'undefined' && CartItems.needsReview)) {
          this.openCartForReview();
          return;
        }

        // Capture exactly what the shopper selected before joining the shared FIFO.
        // A later size change must not alter an already queued add.
        const cartIntent = new FormData(this.form);
        this.submitInProgress = true;
        let linesUpdateDeferred;
        let requestStarted = false;
        let added = false;
        let timeout;

        try {
          this.handleErrorMessage();
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner')?.classList.remove('hidden');

          if (!window.MonoreeCart) throw new Error('Cart queue could not be loaded');
          await window.MonoreeCart.enqueue(async () => {
          try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = cartIntent;
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.cart = cart && ['getSectionsToRender', 'setActiveElement', 'renderContents'].every(
            (method) => typeof cart[method] === 'function'
          ) ? cart : null;
          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          linesUpdateDeferred = this.runCartEvent(() => this.createCartLinesUpdateEvent(variantId, quantity));
          const controller = new AbortController();
          config.signal = controller.signal;
          timeout = setTimeout(() => controller.abort(), 30000);
          requestStarted = true;
          const httpResponse = await fetch(routes.cart_add_url, config);
          const response = await httpResponse.json();

          if (response.status || !httpResponse.ok) {
            requestStarted = httpResponse.status !== 422;
            if (requestStarted) throw new Error(response.description || response.message || window.cartStrings.error);
            this.runCartEvent(() => publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form', productVariantId: variantId,
              errors: response.errors || response.description, message: response.message,
            }));
            this.handleErrorMessage(response.description || response.message || window.cartStrings.error);
            this.runCartEvent(() => this.dispatchCartErrorEvent(response.description || response.message, 'INVALID'));
            this.runCartEvent(() => linesUpdateDeferred?.reject(new Error(response.description || response.message)));
            // 422 may have added the maximum stock. Reconcile, never re-send.
            try {
              await window.MonoreeCart.refresh();
            } catch (error) {
              window.MonoreeCart.block(error);
              throw error;
            }
            this.cartNeedsReview = false;
            window.MonoreeCart.addReviewLink(this.errorMessageWrapper);
            return;
          }

          added = true;
          this.error = false;

          if (!this.cart) {
            this.openCartForReview();
            return;
          }
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            // Keep the FIFO slot until the modal is closed AND the cart is painted.
            await new Promise((resolve, reject) => {
              const finish = () => {
                clearTimeout(timer);
                document.body.removeEventListener('modalClosed', finish);
                resolve();
              };
              const timer = setTimeout(() => {
                document.body.removeEventListener('modalClosed', finish);
                if (!quickAddModal.isConnected || !quickAddModal.hasAttribute('open')) resolve();
                else reject(new Error('Quick-add could not be closed'));
              }, 1500);
              document.body.addEventListener('modalClosed', finish, { once: true });
              quickAddModal.hide(true);
            });
          }
          await this.cart.renderContents(response);
          // A cart page can also contain recommendations; refresh both views together.
          if (document.querySelector('cart-items')) await window.MonoreeCart.refresh();
          this.runCartEvent(() => this.resolveCartLinesUpdate(linesUpdateDeferred));
          this.runCartEvent(() => publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form', productVariantId: variantId, cartData: response, sectionsRendered: true,
          }));
          } catch (error) {
            if (requestStarted || added) window.MonoreeCart.block(error);
            throw error;
          }
          });
        } catch (error) {
          console.error(error);
          this.handleErrorMessage(window.cartStrings.error);
          this.runCartEvent(() => this.dispatchCartErrorEvent(error.message || 'Network error', 'SERVICE_UNAVAILABLE'));
          this.runCartEvent(() => linesUpdateDeferred?.reject(error));
          if (requestStarted || added || window.MonoreeCart?.blocked) {
            this.openCartForReview();
          }
        } finally {
          clearTimeout(timeout);
          this.submitInProgress = false;
          this.submitButton.classList.remove('loading');
          if (this.submitButton.disabled || this.dataset.monoreeVariantPending === 'true') {
            this.submitButton.setAttribute('aria-disabled', 'true');
          } else {
            this.submitButton.removeAttribute('aria-disabled');
          }
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      openCartForReview() {
        window.MonoreeCart?.block(new Error(window.MonoreeCart.text(0)));
        this.cartNeedsReview = true;
        if (typeof CartItems !== 'undefined') CartItems.needsReview = true;
        window.location.assign(window.routes.cart_url);
      }

      runCartEvent(callback) {
        try {
          const result = callback();
          if (result?.catch) result.catch((error) => console.warn(error));
          return result;
        } catch (error) {
          console.warn(error);
          return null;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (!this.submitInProgress) this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        const cartEvent = new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity }],
          promise: deferred.promise,
        });
        this.runCartEvent(() => cartEvent.promise);
        this.dispatchEvent(cartEvent);
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

